import { ChevronRight, MessageCircle, Search, X } from 'lucide-react-native';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text as RNText,
  TextInput,
  View,
} from 'react-native';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ErrorState } from '@/components/ErrorState';
import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';
import type { EveryoneFeedItem, EveryoneFeedResponse } from '@/types/referral';

import { canRemoveItem } from './ReferralDetailSheet';
import { TombstoneRow, WhoClickedRow } from './WhoClickedRow';

/** The three tabs, plus the hidden side reached from the link under the list. */
export type WhoClickedStage = 'started' | 'paid' | 'not_started' | 'removed';

/** A tab the ambassador can text as a group. */
export type WhoClickedTextableStage = 'started' | 'not_started';

export interface WhoClickedFilters {
  stage: WhoClickedStage;
  /** Search query — substring match on row name. `null` or empty = no filter. */
  search: string | null;
}

export const DEFAULT_FILTERS: WhoClickedFilters = {
  stage: 'started',
  search: null,
};

/**
 * The numbers the tab panel prints. From `/referrals/stats` (scoped the same
 * way as the feed); null while they load or if they failed, in which case
 * the panel prints only what the feed's own counts can vouch for.
 */
export interface WhoClickedStats {
  /** Friends with a confirmed step and nothing paid: "one step away". */
  inProgressCount: number;
  leftOnTableCents: number;
  earnedCents: number;
}

interface WhoClickedCardProps {
  /** Card header. Defaults to "Your friends". */
  title?: string;
  items: EveryoneFeedItem[];
  chipCounts: EveryoneFeedResponse['chip_counts'];
  filters: WhoClickedFilters;
  /** Replace the full filter set — tab tap, search change, the Removed link. */
  onFiltersChange: (filters: WhoClickedFilters) => void;
  stats: WhoClickedStats | null;
  /** What the friends were sent to do: "SoFi Checking" on a deal, "a deal" on the invite (which spans every deal). */
  programName: string;
  /** "Text all" on the Started / Not started panels: opens the nudge flow scoped to that tab. */
  onTextAll: (stage: WhoClickedTextableStage) => void;
  /** Single-row nudge — opens the nudge flow with this person preselected. */
  onNudge: (item: EveryoneFeedItem) => void;
  /** Row body tap — opens the detail sheet. */
  onOpenDetail: (item: EveryoneFeedItem) => void;
  /** Undo a removal while its tombstone is still showing. */
  onUndoRemove: (item: EveryoneFeedItem) => void;
  /**
   * Swipe-left asks to remove. It does NOT remove — the parent opens the
   * detail sheet already in its confirm state, so swipe and the sheet share
   * one confirmation surface instead of having two that can drift.
   */
  onRequestRemove: (item: EveryoneFeedItem) => void;
  /**
   * Referral ids removed optimistically and still showing an undo
   * tombstone. They keep their position in `items` so the list doesn't
   * jump while undo is on offer.
   */
  tombstonedIds: ReadonlySet<number>;
  /** Rows shown before "See all N". Defaults 5. */
  initialLimit?: number;
  /**
   * The feed for the current tab is in flight and nothing has arrived yet.
   * Draws a spinner where the rows go, instead of "No one is at this step"
   * for a tab that has not answered.
   */
  loading?: boolean;
  /** The feed failed. Drawn in place of the rows, with a retry. */
  error?: string | null;
  onRetry?: () => void;
  /**
   * Whether rows can be removed here (swipe, and the sheet's Remove). The
   * invite screen passes false: its feed is every friend, and a row hidden
   * there would hide everywhere. Defaults true.
   */
  removable?: boolean;
}

/**
 * The Refer deal screen's friends list (mock P1e, decided 2026-09-13).
 * Three stage tabs — Not started · Started · Paid — each with a bordered
 * panel that says what the tab is worth and offers one group text, then
 * the rows. The tabs replaced the old chip row and the orange "N friends
 * are this close" callout, which counted every unpaid signup as one step
 * away.
 *
 * Stages are the server's (`progress.stage`; `chip_counts.started` /
 * `not_started`), the same for Checking and Invest: Not started = no
 * postback yet; Started = any non-paying postback, rejected included;
 * Paid = the paying conversion. Order within a tab is the server's too
 * (`sort=last_update`): Started stalest-first so overdue rows sit on top,
 * Paid newest, Not started newest signup. Search stays; no sort control.
 */
export const WhoClickedCard = observer(function WhoClickedCard({
  title,
  items,
  chipCounts,
  filters,
  onFiltersChange,
  stats,
  programName,
  onTextAll,
  onNudge,
  onOpenDetail,
  onUndoRemove,
  onRequestRemove,
  tombstonedIds,
  initialLimit = 5,
  removable = true,
  loading = false,
  error = null,
  onRetry,
}: WhoClickedCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [searchOpen, setSearchOpen] = useState(Boolean(filters.search));

  const visible = expanded ? items : items.slice(0, initialLimit);
  const hasOverflow = items.length > initialLimit;
  const counts = tabCountsFor(chipCounts);

  const switchStage = (next: WhoClickedStage) => {
    if (next !== filters.stage) setExpanded(false);
    onFiltersChange({ ...filters, stage: next });
  };

  const closeSearch = () => {
    setSearchOpen(false);
    if (filters.search) onFiltersChange({ ...filters, search: null });
  };

  return (
    <Card variant="flat" padding={0} style={styles.card}>
      <View style={styles.header}>
        <View style={styles.headerTitle}>
          <Text style={styles.title}>{title ?? 'Your friends'}</Text>
          {chipCounts.all > 0 ? (
            <Text style={styles.titleCount} testID="who-clicked-total">
              {chipCounts.all}
            </Text>
          ) : null}
        </View>
        <Pressable
          onPress={searchOpen ? closeSearch : () => setSearchOpen(true)}
          accessibilityRole="button"
          accessibilityLabel={searchOpen ? 'Close search' : 'Search friends'}
          accessibilityState={{ expanded: searchOpen }}
          hitSlop={6}
          style={({ pressed }) => [styles.searchButton, pressed && styles.pressed]}
          testID="whos-clicked-search-toggle"
        >
          {searchOpen ? (
            <X size={16} color={colors.deepGreen} strokeWidth={2.25} />
          ) : (
            <Search size={16} color={colors.deepGreen} strokeWidth={2.25} />
          )}
        </Pressable>
      </View>

      {searchOpen ? (
        <View style={styles.searchInputWrap}>
          <Search size={18} color={colors.textMuted} strokeWidth={2} />
          <TextInput
            value={filters.search ?? ''}
            onChangeText={(text) => onFiltersChange({ ...filters, search: text || null })}
            placeholder="Search by name"
            placeholderTextColor={colors.textMuted}
            style={styles.searchInput}
            returnKeyType="search"
            autoCapitalize="none"
            autoCorrect={false}
            autoFocus
            accessibilityLabel="Search friends"
            testID="whos-clicked-search"
          />
        </View>
      ) : null}

      <View style={styles.tabs} accessibilityRole="tablist" testID="who-clicked-tabs">
        {/* Funnel order, left to right. Started still lands first (it is
            where the money is), so the selected tab is the middle one —
            normal on iOS. The earlier order put the default tab first,
            which was the wrong reason (Jeff, 2026-09-17). */}
        <StageTab
          label="Not started"
          count={counts.notStarted}
          active={filters.stage === 'not_started'}
          tone="default"
          onPress={() => switchStage('not_started')}
        />
        <StageTab
          label="Started"
          count={counts.started}
          active={filters.stage === 'started'}
          tone="default"
          onPress={() => switchStage('started')}
        />
        <StageTab
          label="Paid"
          count={counts.paid}
          active={filters.stage === 'paid'}
          tone="paid"
          onPress={() => switchStage('paid')}
        />
      </View>

      <StagePanel
        stage={filters.stage}
        counts={counts}
        removedCount={chipCounts.removed}
        stats={stats}
        programName={programName}
        listEmpty={items.length === 0}
        onTextAll={onTextAll}
      />

      {visible.length === 0 ? (
        error ? (
          <ErrorState
            bare
            title="Couldn't load your friends"
            body="Pull down to refresh, or tap retry."
            onRetry={onRetry ?? (() => {})}
            testID="who-clicked-error"
          />
        ) : loading ? (
          <ActivityIndicator
            color={colors.deepGreen}
            style={styles.loading}
            testID="who-clicked-loading"
          />
        ) : (
          <Text
            variant="caption"
            color="textMuted"
            style={styles.emptyHint}
            testID="who-clicked-empty"
          >
            {emptyHintFor(filters, chipCounts.all)}
          </Text>
        )
      ) : (
        <View style={styles.list}>
          {visible.map((item, index) =>
            tombstonedIds.has(item.referral_id) ? (
              <TombstoneRow
                key={`${item.referral_id}`}
                item={item}
                isLast={index === visible.length - 1}
                onUndo={() => onUndoRemove(item)}
              />
            ) : (
              <WhoClickedRow
                key={`${item.referral_id}`}
                item={item}
                isLast={index === visible.length - 1}
                onNudge={() => onNudge(item)}
                onPress={() => onOpenDetail(item)}
                onUndo={removable && item.hidden ? () => onUndoRemove(item) : undefined}
                // Swipe only where removal is actually allowed — the ONE
                // predicate the sheet uses, which carries the server's
                // per-row `can_hide` (a friend paid on another deal can't be
                // removed here). A swipe the server would refuse is a dead
                // gesture ending in a "Couldn't remove" toast.
                onSwipeRemove={
                  removable && canRemoveItem(item) ? () => onRequestRemove(item) : undefined
                }
              />
            ),
          )}
          {hasOverflow ? (
            <Pressable
              onPress={() => setExpanded((e) => !e)}
              accessibilityRole="button"
              accessibilityLabel={expanded ? 'Show fewer' : `See all ${items.length}`}
              testID="who-clicked-view-all"
              style={({ pressed }) => [styles.viewAllRow, pressed && styles.pressed]}
            >
              <Text variant="captionBold" color="deepGreen">
                {expanded ? 'Show fewer' : `See all ${items.length}`}
              </Text>
              <ChevronRight
                size={14}
                color={colors.deepGreen}
                strokeWidth={2.25}
                style={expanded ? styles.viewAllChevronOpen : undefined}
              />
            </Pressable>
          ) : null}
        </View>
      )}

      {/* The hidden side, reachable only once something has been removed —
          the escape hatch that outlives the undo tombstone. Only where
          removing is possible: the invite screen cannot undo, so a link to
          a list of UNDO pills that do nothing is not offered there. */}
      {removable && chipCounts.removed > 0 && filters.stage !== 'removed' ? (
        <Pressable
          onPress={() => switchStage('removed')}
          accessibilityRole="button"
          accessibilityLabel={`See ${chipCounts.removed} removed`}
          hitSlop={12}
          style={({ pressed }) => [styles.removedLink, pressed && styles.pressed]}
          testID="who-clicked-removed-link"
        >
          <Text variant="caption" color="textMuted">
            {`Removed · ${chipCounts.removed}`}
          </Text>
        </Pressable>
      ) : null}
    </Card>
  );
});

interface TabCounts {
  started: number;
  paid: number;
  notStarted: number;
}

/**
 * Tab counts from the server's chips. An older server sends no
 * `started` / `not_started`; then everything unpaid sits under Started,
 * which is what that server can vouch for — and `everyoneFeedStore`
 * matches by asking that server for `in_progress` when the Started tab is
 * selected, so the count and the list below it agree.
 */
export function tabCountsFor(chipCounts: EveryoneFeedResponse['chip_counts']): TabCounts {
  const notStarted = chipCounts.not_started ?? 0;
  const started = chipCounts.started ?? Math.max(chipCounts.in_progress - notStarted, 0);
  return { started, paid: chipCounts.paid, notStarted };
}

interface StageTabProps {
  label: string;
  count: number;
  active: boolean;
  tone: 'default' | 'paid';
  onPress: () => void;
}

function StageTab({ label, count, active, tone, onPress }: StageTabProps) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="tab"
      accessibilityLabel={`${label}, ${count}`}
      accessibilityState={{ selected: active }}
      style={({ pressed }) => [
        styles.tab,
        active && styles.tabActive,
        pressed && !active && styles.pressed,
      ]}
    >
      {/* No adjustsFontSizeToFit: iOS applied it to "Paid" — the shortest
          label — and drew it at ~7pt beside two normal tabs (sim,
          2026-09-18). The three labels fit their thirds at 14pt on a
          375pt screen, so nothing needs to shrink. */}
      <RNText style={[styles.tabLabel, active && styles.tabLabelActive]} numberOfLines={1}>
        {label}
      </RNText>
      <Text
        style={[
          styles.tabCount,
          active && (tone === 'paid' ? styles.tabCountPaid : styles.tabCountActive),
        ]}
      >
        {count}
      </Text>
    </Pressable>
  );
}

interface StagePanelProps {
  stage: WhoClickedStage;
  counts: TabCounts;
  removedCount: number;
  stats: WhoClickedStats | null;
  programName: string;
  /**
   * Whether the list below has no rows at all, tombstones included. The
   * server's count drops to zero the moment the last row is removed, while
   * its tombstone still offers Undo for eight seconds; hiding the panel
   * then would shift Undo under the user's finger.
   */
  listEmpty: boolean;
  onTextAll: (stage: WhoClickedTextableStage) => void;
}

/**
 * The bordered, tinted panel under the tabs: what this tab is worth and
 * the one thing to do about it. Hidden once the tab is empty — the list's
 * own empty line says so.
 */
function StagePanel({
  stage,
  counts,
  removedCount,
  stats,
  programName,
  listEmpty,
  onTextAll,
}: StagePanelProps) {
  const copy = panelCopyFor({ stage, counts, removedCount, stats, programName, listEmpty });
  if (!copy) return null;
  return (
    <View style={[styles.panel, PANEL_STYLES[copy.tone]]} testID={`who-clicked-panel-${stage}`}>
      <View style={styles.panelText}>
        <Text style={styles.panelTitle}>{copy.title}</Text>
        {copy.body ? <Text style={styles.panelBody}>{copy.body}</Text> : null}
      </View>
      {/* Web has no Messages composer (the nudge screen's send is disabled
          there, as is the sheet's Text button), so the group text is not
          offered on web either. */}
      {copy.textAll && Platform.OS !== 'web' ? (
        <Button
          label="Text all"
          variant={copy.textAll === 'started' ? 'dark' : 'outline'}
          size="sm"
          leadingIcon={MessageCircle}
          onPress={() => onTextAll(copy.textAll!)}
          hitSlop={8}
          style={styles.panelButton}
          testID={`who-clicked-text-all-${copy.textAll}`}
        />
      ) : null}
    </View>
  );
}

interface PanelCopy {
  tone: 'default' | 'paid';
  title: string;
  /** One factual line under the title. Omit when the title says it all. */
  body?: string;
  textAll: WhoClickedTextableStage | null;
}

export function panelCopyFor({
  stage,
  counts,
  removedCount,
  stats,
  programName,
  listEmpty = true,
}: Omit<StagePanelProps, 'onTextAll' | 'listEmpty'> & { listEmpty?: boolean }): PanelCopy | null {
  switch (stage) {
    case 'started': {
      if (counts.started === 0 && listEmpty) return null;
      // "One step away" is the stats' number: friends with a confirmed step
      // and nothing paid. The tab also holds rejected rows, so the two can
      // differ; the money is the headline when there is some, the tab's
      // own count otherwise (never "0 friends started" above a full tab).
      const n = stats?.inProgressCount ?? 0;
      const cents = stats?.leftOnTableCents ?? 0;
      return {
        tone: 'default',
        title:
          cents > 0 && n > 0
            ? `${dollars(cents)} waiting on ${n}`
            : `${counts.started} ${plural(counts.started, 'friend')} started`,
        body: `They started ${programName} but haven't finished.`,
        textAll: 'started',
      };
    }
    case 'paid': {
      if (counts.paid === 0 && listEmpty) return null;
      const earned = stats?.earnedCents ?? 0;
      return {
        tone: 'paid',
        title:
          earned > 0
            ? `${dollars(earned)} earned from ${counts.paid} ${plural(counts.paid, 'friend')}`
            : `${counts.paid} ${plural(counts.paid, 'friend')} paid you`,
        // No body: "Most recent first." described the sort order, which
        // nobody asked about, and read as a stray note (Jeff, 2026-09-17).
        textAll: null,
      };
    }
    case 'not_started':
      if (counts.notStarted === 0 && listEmpty) return null;
      return {
        tone: 'default',
        title: `${counts.notStarted} joined but never started ${programName}`,
        // Plain statement of what "not started" means. The earlier "One
        // group text, then let them go." editorialised about giving up on
        // people and nobody understood "group text" (Jeff, 2026-09-17).
        body: `They signed up through your link but haven't opened ${programName}.`,
        textAll: 'not_started',
      };
    case 'removed':
      if (removedCount === 0 && listEmpty) return null;
      return {
        tone: 'default',
        title: `${removedCount} removed`,
        body: 'They keep earning if they finish. Undo puts them back.',
        textAll: null,
      };
  }
}

function dollars(cents: number): string {
  return `$${Math.floor(cents / 100).toLocaleString('en-US')}`;
}

function plural(n: number, noun: string): string {
  return n === 1 ? noun : `${noun}s`;
}

function emptyHintFor(filters: WhoClickedFilters, total: number): string {
  if (filters.search) return `No matches for "${filters.search}".`;
  if (filters.stage === 'removed') return "You haven't removed anyone.";
  // A brand-new ambassador: nothing is "at a step" yet, the list is empty
  // because nobody has come through the link.
  if (total === 0) return 'Nobody has come through your link yet. Share it to get started.';
  // Each tab says what its emptiness MEANS. "No one is at this step" under
  // Paid read as a shrug on the one tab that is about money.
  switch (filters.stage) {
    case 'paid':
      return 'No payouts yet. The first friend who finishes shows up here.';
    case 'started':
      return "No one is mid-way right now. Friends who open the deal but haven't finished land here.";
    case 'not_started':
      return 'Everyone who signed up has started. Nice.';
    default:
      return 'No one is at this step.';
  }
}

const styles = StyleSheet.create({
  card: {
    padding: spacing.lg,
    gap: spacing.md,
    // A swiped row translates left and its action pane sits to the right of
    // the row's own bounds. Without clipping, both render outside the card
    // entirely — the name slides out over the page background and the red
    // pane hangs off the right edge. Safe here: `flat` carries no shadow.
    overflow: 'hidden',
  },
  pressed: {
    opacity: 0.7,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: spacing.sm,
  },
  title: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.xl,
    lineHeight: 26,
    color: colors.deepGreen,
  },
  titleCount: {
    fontFamily: fonts.body,
    fontSize: fontSizes.base,
    color: colors.textMuted,
  },
  searchButton: {
    width: 36,
    height: 36,
    borderRadius: radii.full,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.base,
    minHeight: 44,
    borderRadius: radii.full,
    backgroundColor: colors.surface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  searchInput: {
    flex: 1,
    fontFamily: fonts.body,
    fontSize: fontSizes.base,
    color: colors.deepGreen,
    padding: 0,
  },
  // iOS segmented-control chrome (mock P1e): a 3pt track around 10pt-radius
  // segments 2pt apart. These are the control's own proportions, not layout
  // spacing, so they sit off the 4/8 scale on purpose — the same documented
  // drift as the 14pt panel radius below.
  tabs: {
    flexDirection: 'row',
    gap: 2,
    padding: 3,
    borderRadius: 12,
    backgroundColor: colors.neutralPillBg,
  },
  // Label over count: "Not started 201" side by side needs ~126pt and a
  // 375pt screen gives each tab ~89pt.
  tab: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    paddingVertical: spacing.sm,
    paddingHorizontal: 4,
    borderRadius: radii.base,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'transparent',
  },
  tabActive: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    shadowColor: colors.charcoal,
    shadowOpacity: 0.1,
    shadowRadius: 3,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },
  tabLabel: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    lineHeight: 18,
    color: colors.textMuted,
  },
  tabLabelActive: {
    fontFamily: fonts.bodyBold,
    color: colors.deepGreen,
  },
  tabCount: {
    fontFamily: fonts.numeric,
    fontSize: fontSizes.xs,
    lineHeight: 14,
    color: colors.textMuted,
  },
  tabCountActive: {
    color: colors.deepGreen,
  },
  tabCountPaid: {
    color: colors.accentText,
  },
  panel: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingVertical: 14,
    paddingHorizontal: spacing.base,
    // 14: this card's panel radius (mock P1e), between radii.base and
    // radii.lg. Documented drift; the sheet's status card matches.
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
  },
  panelDefault: {
    backgroundColor: colors.neutralPillBg,
    borderColor: colors.border,
  },
  panelPaid: {
    backgroundColor: colors.paidPillBg,
    borderColor: colors.paidPillBorder,
  },
  panelText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  panelTitle: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.base,
    lineHeight: 20,
    color: colors.deepGreen,
  },
  panelBody: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    lineHeight: 18,
    color: colors.charcoal,
  },
  panelButton: {
    flexShrink: 0,
  },
  emptyHint: {
    paddingVertical: spacing.md,
    textAlign: 'center',
  },
  loading: {
    paddingVertical: spacing.lg,
  },
  list: {
    // Full-bleed: rows run to the card's edge with hairline dividers, no box
    // inside the card. Gives each row the card's full width (a boxed list
    // cost ~30pt per row) and lets the swipe pane reach the edge; rows add
    // the card's inset back on their own content.
    marginHorizontal: -spacing.lg,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
    overflow: 'hidden',
  },
  viewAllRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    // 44pt: 16 + 18 line + 16.
    paddingVertical: spacing.base,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
  },
  viewAllChevronOpen: {
    transform: [{ rotate: '-90deg' }],
  },
  removedLink: {
    alignSelf: 'center',
    paddingVertical: spacing.xs,
  },
});

// Declared after `styles` — references it at module load.
const PANEL_STYLES = {
  default: styles.panelDefault,
  paid: styles.panelPaid,
} as const;
